import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ChevronsUpDown } from 'lucide-react';
import * as React from 'react';

export type Option = string | { value: string; label: string };

interface MultiSelectFilterProps {
  label: string;
  options: Option[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  placeholder?: string;
}

export const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
  label,
  options,
  selectedValues,
  onSelectionChange,
  placeholder = 'Select...',
}) => {
  const [open, setOpen] = React.useState(false);

  // Normalize options to objects
  const normalizedOptions = React.useMemo(() => {
    return options.map((opt) => (typeof opt === 'string' ? { value: opt, label: opt } : opt));
  }, [options]);

  const toggleValue = (value: string) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    onSelectionChange(newValues);
  };

  const handleSelectAll = () => {
    onSelectionChange(normalizedOptions.map((o) => o.value));
  };

  const handleClearAll = () => {
    onSelectionChange([]);
  };

  const getSelectedLabels = () => {
    return normalizedOptions
      .filter((opt) => selectedValues.includes(opt.value))
      .map((opt) => opt.label)
      .join(', ');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                aria-expanded={open}
                aria-haspopup="listbox"
                className="w-[200px] justify-between"
              >
                {selectedValues.length > 0 ? (
                  <span className="truncate">{selectedValues.length} selected</span>
                ) : (
                  <span className="text-muted-foreground font-normal">{placeholder}</span>
                )}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          {selectedValues.length > 0 && (
            <TooltipContent>
              <p className="max-w-[300px] break-words text-xs">{getSelectedLabels()}</p>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput placeholder={`Search ${label.toLowerCase()}...`} />
          <div className="flex items-center gap-2 p-2 border-b">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 flex-1 text-xs"
              onClick={handleSelectAll}
            >
              Select All
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 flex-1 text-xs"
              onClick={handleClearAll}
            >
              Clear All
            </Button>
          </div>
          <CommandList>
            <CommandEmpty>No {label.toLowerCase()} found.</CommandEmpty>
            <CommandGroup>
              {normalizedOptions.map((option) => {
                const isSelected = selectedValues.includes(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    value={option.value} // Use value for search filtering
                    onSelect={() => toggleValue(option.value)}
                  >
                    <Checkbox checked={isSelected} className="mr-2 pointer-events-none" />
                    <span className="truncate">{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
